import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { setupStackingSections } from "@/lib/stackSections";

gsap.registerPlugin(ScrollTrigger);

const ric = (cb: () => void) =>
  typeof requestIdleCallback !== "undefined"
    ? requestIdleCallback(cb, { timeout: 200 })
    : (setTimeout(cb, 50) as unknown as number);

const cancelRic = (id: number) =>
  typeof cancelIdleCallback !== "undefined"
    ? cancelIdleCallback(id)
    : clearTimeout(id);

export function useGsapAnimations() {
  useEffect(() => {
    // Phase 1: Hero animations — run immediately (non-blocking, fast)
    const heroContent = document.querySelector("#hero-content");
    if (heroContent) {
      gsap.from(heroContent.children, {
        y: 30,
        opacity: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: "power3.out",
      });
    }

    const heroBadge = document.querySelector("#hero-badge");
    if (heroBadge) {
      gsap.from(heroBadge, {
        scale: 0.8,
        opacity: 0,
        duration: 1,
        ease: "back.out(1.4)",
        delay: 0.6,
      });
      gsap.to(heroBadge, {
        y: -12,
        duration: 2.5,
        yoyo: true,
        repeat: -1,
        ease: "sine.inOut",
      });
    }

    // Phase 2: Defer heavy ScrollTrigger work to idle time
    let ctx: gsap.Context | null = null;
    let refreshTimeout: ReturnType<typeof setTimeout>;

    const idleId = ric(() => {
      ctx = gsap.context(() => {
        // Counter animation
        const counterEl = document.querySelector(".gsap-counter");
        if (counterEl) {
          const obj = { val: 0 };
          gsap.to(obj, {
            scrollTrigger: {
              trigger: counterEl,
              start: "top 90%",
              toggleActions: "play none none none",
            },
            val: 40,
            duration: 2,
            ease: "power1.out",
            onUpdate: () => {
              const span = counterEl.querySelector("span");
              if (span) span.textContent = String(Math.round(obj.val));
            },
          });
        }

        setupStackingSections();
      });

      refreshTimeout = setTimeout(() => ScrollTrigger.refresh(), 600);
    });

    return () => {
      cancelRic(idleId);
      clearTimeout(refreshTimeout);
      ctx?.revert();
    };
  }, []);
}
